import { useState, useRef, useLayoutEffect, useEffect, type ReactElement } from 'react';
import { MoreVertical, Pin, PinOff, Mail, Users, BellOff, Bell, EyeOff, Eye, UserPlus, UserCheck, MessageSquarePlus, Loader2, Tag, Flag, Snowflake, IdCard } from 'lucide-react';
import { parseSPDate, formatDataSP } from '@fiodecobre/shared';
import { Avatar } from '../../../components/Avatar';
import { BadgeEtiqueta } from './BadgeEtiqueta';
import { useToast } from '../../../components/Toast';
import { useDialog } from '../../../components/DialogProvider';
import { useAuth } from '../../../lib/auth';
import { atendimentoApi } from '../lib/api';
import { statusInfo, STATUS_CHAMADO, STATUS_INFO } from '../lib/statusChamado';
import type { Agente, Contato, Conversa } from '../lib/tipos';

type AgenteDisponivel = Pick<Agente, 'id' | 'nome' | 'foto'>;

interface Props {
  conversas: Conversa[];
  ativaId: number | null;
  onSelecionar: (c: Conversa) => void;
  onAtualizar: (patch: Partial<Conversa> & { id: number }) => void;
  onSalvarComoCliente?: (c: Conversa) => void;
  /** Abre o bloco SOBRE O CLIENTE (nome + etiqueta) sem passar pela tela do fly. */
  onEtiquetar?: (c: Conversa) => void;
  onPerfil?: (c: Conversa) => void;
  contatosSemConversa?: Contato[];
  onIniciarConversaComContato?: (c: Contato) => void;
  abrindoContato?: boolean;
}

/**
 * Linha "Titulo: VALOR" do item do Atendimento; sem valor, o titulo fica e o valor vira "—".
 * `destaque` vira etiqueta azul escuro com letra amarela negrito (a Origem, pedidos dele em
 * 23/10/2026). O azul (#1e3a8a) e' mais claro que o fundo da lista, senao a etiqueta some nele.
 */
function LinhaTitulo({ titulo, valor, destaque }: { titulo: string; valor: string | null | undefined; destaque?: boolean }) {
  return (
    <div className="text-[11px] leading-[18px] truncate" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }}>
      {titulo}{' '}
      {valor
        ? <b
            className={destaque ? 'px-1.5 py-px rounded' : undefined}
            style={destaque ? { color: '#fde68a', background: '#1e3a8a', fontWeight: 700 } : { color: 'var(--wa-text-fg, #e5e7eb)', fontWeight: 600 }}
          >{valor}</b>
        : '—'}
    </div>
  );
}

function tempoRelativo(iso: string | null): string {
  if (!iso) return '';
  // Backend grava como TIMESTAMP sem TZ com wall-clock SP — parseSPDate corrige a leitura.
  const d = parseSPDate(iso);
  if (!d) return '';
  const agora = new Date();
  const ms = agora.getTime() - d.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const dias = Math.floor(h / 24);
  if (dias < 7) return `${dias}d`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export function ListaConversas({ conversas, ativaId, onSelecionar, onAtualizar, onSalvarComoCliente, onEtiquetar, onPerfil, contatosSemConversa, onIniciarConversaComContato, abrindoContato }: Props) {
  const [menuId, setMenuId] = useState<number | null>(null);
  // Posicao do menu da conversa em coordenadas de TELA. Ele nasceu `absolute` dentro da
  // lista, que e um `overflow-y-auto`: nas ultimas conversas visiveis o menu era clipado
  // pela moldura e os itens de baixo — Redirecionar e Alterar status, justamente os que
  // AGEM — simplesmente nao existiam, sem barra pra rolar ate eles. O Ruan relatou como
  // "sumiu a opcao de direcionar chamado" (11/08) e, ao abrir o menu numa conversa mais
  // acima, ela "voltou". Menu dentro de container com scroll usa `fixed` + medida real.
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [atribuirId, setAtribuirId] = useState<number | null>(null);
  const [atribuirInbox, setAtribuirInbox] = useState<string | null>(null);
  const [agentes, setAgentes] = useState<AgenteDisponivel[]>([]);
  const [semPontoHoje, setSemPontoHoje] = useState(false);
  const [statusId, setStatusId] = useState<number | null>(null);
  // Conversa que foi aberta NAO LIDA: abrir zera o contador, e sem isto ela desceria pra
  // posicao da data no mesmo clique, saindo de baixo do dedo de quem vai le-la. Fica no
  // grupo das nao lidas ate outra conversa ser aberta (pedido do Ruan, 10/10/2026).
  const abertaNaoLidaRef = useRef<number | null>(null);
  const { usuario } = useAuth();
  const toast = useToast();
  const dialog = useDialog();
  const ehAdmin = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
  // Ocultar/Mostrar e so de admin — gerente fica de fora (pedido do Ruan, 01/10/2026).
  const podeArquivar = usuario?.perfil === 'admin';

  function fecharMenu() {
    setMenuId(null);
    setMenuPos(null);
  }

  function abrirMenu(e: React.MouseEvent<HTMLButtonElement>, convId: number) {
    e.stopPropagation();
    if (menuId === convId) return fecharMenu();
    const r = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
    setMenuId(convId);
  }

  // Empurra o menu pra cima quando ele passa do rodape da TELA. Mede o proprio elemento
  // ja renderizado em vez de estimar altura por contagem de itens — o menu muda de
  // tamanho conforme o perfil (admin ve dois itens a mais) e conforme a conversa (grupo
  // nao tem "Salvar como cliente"), entao numero cravado erraria justo no caso raro.
  useLayoutEffect(() => {
    if (menuId === null) return;
    const el = menuRef.current;
    if (!el) return;
    const excesso = el.getBoundingClientRect().bottom - (window.innerHeight - 8);
    if (excesso > 0) setMenuPos((p) => (p ? { ...p, top: Math.max(8, p.top - excesso) } : p));
  }, [menuId]);

  // `fixed` nao acompanha o scroll da lista nem o redimensionamento: em vez de deixar o
  // menu flutuando longe da conversa que o abriu, ele fecha.
  useEffect(() => {
    if (menuId === null) return;
    window.addEventListener('resize', fecharMenu);
    return () => window.removeEventListener('resize', fecharMenu);
  }, [menuId]);

  const temContatosExtra = !!(contatosSemConversa && contatosSemConversa.length > 0 && onIniciarConversaComContato);

  if (!conversas.length && !temContatosExtra) {
    return <div className="text-center text-sm py-8" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }}>Nenhuma conversa</div>;
  }

  // Ordem da lista (pedido do Ruan, 10/10/2026): fixadas, depois NAO LIDAS, depois o resto —
  // cada grupo pela data. Antes as nao lidas ficavam espalhadas entre as lidas, e o disparo
  // de campanha empurrava a mensagem do cliente pra baixo da dobra.
  const grupo = (c: Conversa) =>
    c.fixada ? 0 : c.nao_lidas > 0 || (c.id === ativaId && c.id === abertaNaoLidaRef.current) ? 1 : 2;
  const ordenadas = [...conversas].sort((a, b) =>
    grupo(a) - grupo(b)
    || new Date(b.ultima_mensagem_em || 0).getTime() - new Date(a.ultima_mensagem_em || 0).getTime());

  async function alternar(c: Conversa, campo: 'fixada' | 'silenciada' | 'arquivada') {
    setMenuId(null);
    const novo = !c[campo];
    // Ocultar pede confirmacao com o NOME na tela: arquivada nao volta sozinha com
    // mensagem nova (decisao do Ruan, 01/10/2026), entao um clique na conversa errada
    // esconde o cliente ate alguem achar em Arquivados — foi o caso do Gabriel (#75966).
    if (campo === 'arquivada' && novo) {
      const ct = c.contato;
      const nome = [ct.nome, ct.sobrenome].filter(Boolean).join(' ') || ct.push_name || 'Desconhecido';
      const ok = await dialog.confirmar(
        `Ocultar a conversa de ${nome}${ct.telefone ? ` (${ct.telefone})` : ''}?\n\n`
        + 'Ela sai da lista e vai para Arquivados. Mensagens novas deste contato NAO trazem a conversa de volta: '
        + 'ela so reaparece clicando em Mostrar, dentro de Arquivados.',
        { titulo: 'Ocultar conversa?', textoConfirmar: 'Ocultar' },
      );
      if (!ok) return;
    }
    try {
      await atendimentoApi.atualizarConversa(c.id, { [campo]: novo });
      onAtualizar({ id: c.id, [campo]: novo } as Partial<Conversa> & { id: number });
      const labels = { fixada: ['Fixada', 'Desafixada'], silenciada: ['Silenciada', 'Notificacoes reativadas'], arquivada: ['Arquivada', 'Visivel'] };
      toast.sucesso(novo ? labels[campo][0]! : labels[campo][1]!);
    } catch {
      toast.erro('Erro ao atualizar');
    }
  }

  async function marcarNaoLida(c: Conversa) {
    setMenuId(null);
    try {
      const r = await atendimentoApi.marcarNaoLida(c.id);
      onAtualizar({ id: c.id, nao_lidas: r.nao_lidas });
      toast.sucesso('Marcada como nao lida');
    } catch {
      toast.erro('Erro');
    }
  }

  async function abrirAtribuir(c: Conversa) {
    setMenuId(null);
    setAtribuirId(c.id);
    setAtribuirInbox(c.inbox);
    setAgentes([]);
    setSemPontoHoje(false);
    try {
      // Backend filtra por inbox + ativo + ausente_temporario + recebe_chamados + presenca real
      // (bateu ponto hoje) — o mesmo criterio do round-robin.
      const lista = await atendimentoApi.listarAgentesDisponiveis(c.inbox);
      if (lista.length > 0) {
        setAgentes(lista);
        return;
      }
      // Fallback: ninguem bateu ponto. Mostra todas do departamento pra admin/gerente
      // poder pre-atribuir antes do expediente ou apos saidas.
      // NAO filtra `recebe_chamados` de proposito: quem saiu do rodizio esta na loja, e
      // entregar um chamado na mao pra ele e justamente a saida do admin. Ausente (ferias,
      // atestado) continua fora — nesse a pessoa nem esta aqui.
      const todas = await atendimentoApi.listarAgentes();
      const elegiveis = todas
        .filter((a) => a.ativo && !a.ausente_temporario)
        .filter((a) => Array.isArray(a.departamentos) && a.departamentos.includes(c.inbox))
        .map((a) => ({ id: a.id, nome: a.nome, foto: a.foto }));
      setAgentes(elegiveis);
      setSemPontoHoje(elegiveis.length > 0);
    } catch {
      // silencia
    }
  }

  async function atribuir(convId: number, agenteId: number | null) {
    setAtribuirId(null);
    try {
      await atendimentoApi.atualizarConversa(convId, { usuario_atribuido_id: agenteId });
      const ag = agentes.find((a) => a.id === agenteId);
      onAtualizar({ id: convId, usuario_atribuido_id: agenteId, usuario_atribuido_nome: ag?.nome ?? null, usuario_atribuido_login: null });
      toast.sucesso(agenteId ? `Atribuido a ${ag?.nome.split(' ')[0]}` : 'Atribuicao removida');
    } catch {
      toast.erro('Erro ao atribuir');
    }
  }

  async function mudarStatus(convId: number, status: string) {
    setStatusId(null);
    try {
      await atendimentoApi.atualizarStatusChamado(convId, status);
      onAtualizar({ id: convId, status } as Partial<Conversa> & { id: number });
      toast.sucesso(`Status: ${STATUS_INFO[status as keyof typeof STATUS_INFO]?.label ?? status}`);
    } catch {
      toast.erro('Erro ao alterar status');
    }
  }

  return (
    <div className="flex-1 overflow-y-auto" onScroll={() => { if (menuId !== null) fecharMenu(); }}>
      {ordenadas.map((c) => {
        const ativa = c.id === ativaId;
        const ct = c.contato;
        const tempo = tempoRelativo(c.ultima_mensagem_em);
        const podeAtribuir = ehAdmin || c.usuario_atribuido_id === usuario?.id;
        // Quem atende aparecia DUAS vezes na mesma fileira: a pastilha azul do agente
        // atribuido e a etiqueta verde que o `agent-label` cola no contato. Enquanto a
        // etiqueta vivia defasada (ela so era escrita pelo rodizio, ver DA-018) os dois
        // nomes eram diferentes e pareciam informacoes distintas; sincronizadas em 11/08,
        // viraram "JOAO ... JOAO" lado a lado. A pastilha some, a etiqueta REDUNDANTE some,
        // e o nome passa a ter linha propria — a etiqueta de OUTRO agente continua visivel,
        // porque ai ela diz o que a linha nao diz (quem atendeu o contato da ultima vez).
        // Pelo LOGIN desde 15/10/2026 ([DA-812]): e' por ele que o `agent-label` batiza a
        // etiqueta agora. O nome fica de reserva pra resposta sem login.
        const slugAtribuido = (c.usuario_atribuido_login || c.usuario_atribuido_nome)
          ? (c.usuario_atribuido_login || c.usuario_atribuido_nome)!.toLowerCase().replace(/\s+fiodecobre$/, '').trim()
          : null;
        // Duas guardas, e as duas ficam: `ocultar` (migration 0278) tira as que ele marcou
        // no Gerenciar — hoje as seis de agente; o `slugAtribuido` segue cobrindo o caso em
        // que ele DESMARCA uma delas, e sem ele o nome voltaria a aparecer duas vezes.
        const etiquetasVisiveis = (ct.etiquetas ?? []).filter(
          (e) => !e.ocultar && (!slugAtribuido || e.nome.toLowerCase().trim() !== slugAtribuido),
        );
        // O "Direcionado:" mostra o LOGIN (pedido dele, 02/10/2026): o nome do cadastro pode
        // ser outro (login CLAUDIA, nome CLAUDIRENE ...). Sem login na resposta (atribuicao
        // feita agora pelo menu, antes da proxima carga), cai no primeiro nome.
        const primeiroNome = (c.usuario_atribuido_login || c.usuario_atribuido_nome)?.trim().split(' ')[0] ?? '';
        const nomeDirecionado = primeiroNome ? primeiroNome.charAt(0) + primeiroNome.slice(1).toLowerCase() : '';
        const si = statusInfo(c.status);
        return (
          <div key={c.id}>
            <div
              onClick={() => {
                if (c.id !== ativaId) abertaNaoLidaRef.current = c.nao_lidas > 0 ? c.id : null;
                onSelecionar(c);
              }}
              onMouseLeave={() => { if (menuId === c.id) setMenuId(null); }}
              className={`group relative flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${ativa ? 'border-l-2 border-l-blue-400' : 'hover:bg-white/5'}`}
              style={ativa ? { background: 'color-mix(in srgb, var(--wa-sidebar-bg, #1e1e1e) 70%, #ffffff 30%)' } : undefined}
            >
              {ct.grupo && !ct.foto_url ? (
                <div className="w-[42px] h-[42px] rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                  <Users size={20} className="text-white" />
                </div>
              ) : (
                <Avatar nome={ct.nome || ct.push_name || ct.telefone || '?'} fotoUrl={ct.foto_url} tamanho={42} />
              )}

              {/* ATENDIMENTO: uma coluna so, desenho dele aprovado em mockup (23/10/2026). Os
                  quatro titulos aparecem SEMPRE, com "—" quando falta o dado (pedido dele). O
                  status do chamado (DA-099) virou selo ao lado do nome; o "por: X" saiu, e o
                  trecho da ultima mensagem, o tempo e a tag do cliente tambem. A bolinha de nao
                  lidas foi pra ponta da linha do nome. Compras e App seguem com o item de antes. */}
              {c.inbox === 'atendimento' ? (
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-medium text-sm truncate flex items-center gap-1" style={{ color: 'var(--wa-text-fg, #e5e7eb)' }}>
                      {c.fixada && <Pin size={11} className="text-amber-500 flex-shrink-0" />}
                      {c.silenciada && <BellOff size={11} className="flex-shrink-0" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }} />}
                      {c.congelada_em && c.status !== 'encerrado' && (
                        <span className="flex-shrink-0 inline-flex" title="Congelado — continua amanha">
                          <Snowflake size={11} style={{ color: '#38bdf8' }} />
                        </span>
                      )}
                      {[ct.nome, ct.sobrenome].filter(Boolean).join(' ') || ct.push_name || ct.telefone || 'Desconhecido'}
                    </span>
                    {c.contato_eh_agente ? (
                      <span
                        className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold leading-tight"
                        style={{ background: 'var(--panel-bg, #374151)', color: 'var(--subtext-color, #9ca3af)', border: '1px solid rgba(255,255,255,0.15)' }}
                      >
                        EQUIPE
                      </span>
                    ) : (
                      <span
                        className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold leading-tight"
                        style={{ background: si.bg, color: si.fg }}
                        title={c.status_origem ? `por: ${c.status_origem}` : undefined}
                      >
                        {si.label}
                      </span>
                    )}
                    <span className="flex-1" />
                    {c.nao_lidas > 0 && (
                      <span className="flex-shrink-0 bg-blue-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                        {c.nao_lidas}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1 mt-0.5 flex-wrap items-center text-[11px] leading-[18px] min-h-[18px]" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }}>
                    Área:{' '}
                    {etiquetasVisiveis.length > 0
                      ? etiquetasVisiveis.slice(0, 3).map((e) => <BadgeEtiqueta key={e.id} nome={e.nome} cor={e.cor} apelido={e.apelido} />)
                      : <span>—</span>}
                  </div>
                  <LinhaTitulo titulo="Origem:" valor={ct.origem_cliente} destaque />
                  <LinhaTitulo titulo="ÚLTIMA COMPRA:" valor={ct.ultima_compra_em ? formatDataSP(ct.ultima_compra_em) : null} />
                  <LinhaTitulo titulo="Atendente:" valor={nomeDirecionado ? nomeDirecionado.toUpperCase() : null} />
                </div>
              ) : (
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <span className="font-medium text-sm truncate flex items-center gap-1" style={{ color: 'var(--wa-text-fg, #e5e7eb)' }}>
                      {c.fixada && <Pin size={11} className="text-amber-500 flex-shrink-0" />}
                      {c.silenciada && <BellOff size={11} className="flex-shrink-0" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }} />}
                      {c.congelada_em && c.status !== 'encerrado' && (
                        <span className="flex-shrink-0 inline-flex" title="Congelado — continua amanha">
                          <Snowflake size={11} style={{ color: '#38bdf8' }} />
                        </span>
                      )}
                      {[ct.nome, ct.sobrenome].filter(Boolean).join(' ') || ct.push_name || ct.telefone || 'Desconhecido'}
                    </span>
                    {ct.whatsapp_janela_aberta != null && (
                      <span
                        title={ct.whatsapp_janela_aberta ? 'WhatsApp: janela 24h aberta — pode enviar mensagem livre' : 'WhatsApp: janela 24h fechada — so templates'}
                        style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: ct.whatsapp_janela_aberta ? '#3b82f6' : '#9ca3af', boxShadow: '0 0 0 1px rgba(255,255,255,0.3)' }}
                      />
                    )}
                  </div>
                  <span className="text-[11px] flex-shrink-0 ml-2" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }}>{tempo}</span>
                </div>

                <div className="flex items-center justify-between mt-0.5 gap-2">
                  <span className="text-xs truncate" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }}>{c.ultima_mensagem_preview ?? ''}</span>
                  {c.nao_lidas > 0 && (
                    <span className="flex-shrink-0 bg-blue-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {c.nao_lidas}
                    </span>
                  )}
                </div>

                {(ct.cliente_nome || etiquetasVisiveis.length > 0) && (
                  <div className="flex gap-1 mt-1 flex-wrap items-center">
                    {ct.cliente_nome && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold leading-none max-w-full"
                        style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}
                        title={ct.cliente_codigo ? `Cliente #${ct.cliente_codigo}` : 'Cliente vinculado'}
                      >
                        <UserCheck size={10} className="flex-shrink-0" />
                        <span className="truncate">
                          {ct.cliente_nome}{ct.cliente_codigo ? ` · #${ct.cliente_codigo}` : ''}
                        </span>
                      </span>
                    )}
                    {etiquetasVisiveis.slice(0, 3).map((e) => <BadgeEtiqueta key={e.id} nome={e.nome} cor={e.cor} apelido={e.apelido} />)}
                  </div>
                )}

                {nomeDirecionado && (
                  <div className="text-[11px] mt-0.5 leading-tight truncate" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }}>
                    Direcionado: <b style={{ color: 'var(--wa-text-fg, #e5e7eb)', fontWeight: 600 }}>{nomeDirecionado}</b>
                  </div>
                )}
              </div>
              )}

              <button
                onClick={(e) => abrirMenu(e, c.id)}
                className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 p-1.5 rounded-md transition-all shadow-sm"
                style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}
              >
                <MoreVertical size={16} style={{ color: 'var(--wa-text-fg, #e5e7eb)' }} />
              </button>

              {menuId === c.id && menuPos && (
                <div
                  ref={menuRef}
                  className="fixed z-50 bg-white rounded-lg shadow-lg border py-1 min-w-[160px]"
                  style={{ top: menuPos.top, right: menuPos.right }}
                >
                  <ItemMenu icon={c.fixada ? <PinOff size={14} /> : <Pin size={14} />} label={c.fixada ? 'Desafixar' : 'Fixar'} onClick={(e) => { e.stopPropagation(); alternar(c, 'fixada'); }} />
                  <ItemMenu icon={<Mail size={14} />} label="Nao lida" onClick={(e) => { e.stopPropagation(); marcarNaoLida(c); }} />
                  <ItemMenu icon={c.silenciada ? <Bell size={14} className="text-green-500" /> : <BellOff size={14} className="text-gray-500" />} label={c.silenciada ? 'Reativar som' : 'Silenciar'} onClick={(e) => { e.stopPropagation(); alternar(c, 'silenciada'); }} />
                  {podeArquivar && (
                    <ItemMenu icon={c.arquivada ? <Eye size={14} className="text-green-500" /> : <EyeOff size={14} className="text-gray-500" />} label={c.arquivada ? 'Mostrar' : 'Ocultar'} onClick={(e) => { e.stopPropagation(); alternar(c, 'arquivada'); }} />
                  )}
                  {!ct.grupo && onSalvarComoCliente && (
                    <ItemMenu icon={<UserPlus size={14} className="text-green-600" />} label="Salvar como cliente" onClick={(e) => { e.stopPropagation(); setMenuId(null); onSalvarComoCliente(c); }} />
                  )}
                  {podeAtribuir && (
                    <ItemMenu icon={<UserCheck size={14} className="text-blue-600" />} label="Redirecionar" onClick={(e) => { e.stopPropagation(); abrirAtribuir(c); }} />
                  )}
                  {ehAdmin && (
                    <ItemMenu icon={<Flag size={14} className="text-blue-600" />} label="Alterar status" onClick={(e) => { e.stopPropagation(); setMenuId(null); setStatusId(c.id); }} />
                  )}
                  {/* Ultimo item do menu (pedido dele, 24/09/2026). Mesmo alcance do botao Produto
                      (so' atendimento): e' o bloco da tela do fly, e a etiqueta ali e' a area de
                      trabalho do CLIENTE. Sem trava de perfil. */}
                  {!ct.grupo && c.inbox === 'atendimento' && onEtiquetar && (
                    <ItemMenu icon={<Tag size={14} className="text-blue-600" />} label="Etiqueta" onClick={(e) => { e.stopPropagation(); fecharMenu(); onEtiquetar(c); }} />
                  )}
                  {/* Abaixo do Etiqueta (pedido dele, 26/10/2026, com print): Area e Origem, so' em branco. */}
                  {!ct.grupo && c.inbox === 'atendimento' && onPerfil && (
                    <ItemMenu icon={<IdCard size={14} className="text-blue-600" />} label="Perfil" onClick={(e) => { e.stopPropagation(); fecharMenu(); onPerfil(c); }} />
                  )}
                </div>
              )}
            </div>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.12)' }} />
          </div>
        );
      })}

      {temContatosExtra && (
        <>
          <div
            className="px-3 py-1.5 text-[10px] uppercase tracking-wide font-semibold flex items-center gap-1.5"
            style={{
              color: 'var(--wa-subtext-fg, #9ca3af)',
              background: 'color-mix(in srgb, var(--wa-sidebar-bg, #1e1e1e) 85%, #000 15%)',
              borderTop: '1px solid rgba(255,255,255,0.12)',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <MessageSquarePlus size={11} />
            Outros contatos (iniciar conversa)
          </div>
          {contatosSemConversa!.map((ct) => {
            const titulo = [ct.nome, ct.sobrenome].filter(Boolean).join(' ') || ct.push_name || ct.telefone || ct.jid;
            return (
              <div key={`ct-${ct.id}`}>
                <button
                  type="button"
                  onClick={() => onIniciarConversaComContato!(ct)}
                  disabled={abrindoContato}
                  className="w-full flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors hover:bg-white/5 disabled:opacity-60 text-left"
                >
                  <Avatar nome={titulo} fotoUrl={ct.foto_url} tamanho={42} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-sm truncate" style={{ color: 'var(--wa-text-fg, #e5e7eb)' }}>{titulo}</span>
                      {ct.whatsapp_janela_aberta != null && (
                        <span
                          title={ct.whatsapp_janela_aberta ? 'WhatsApp: janela 24h aberta — pode enviar mensagem livre' : 'WhatsApp: janela 24h fechada — so templates'}
                          style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: ct.whatsapp_janela_aberta ? '#3b82f6' : '#9ca3af', boxShadow: '0 0 0 1px rgba(255,255,255,0.3)' }}
                        />
                      )}
                    </div>
                    <div className="text-xs truncate" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }}>{ct.telefone || ct.jid}</div>
                  </div>
                  {abrindoContato ? (
                    <Loader2 size={14} className="animate-spin flex-shrink-0" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }} />
                  ) : (
                    <MessageSquarePlus size={14} className="flex-shrink-0" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }} />
                  )}
                </button>
                <div style={{ height: 1, background: 'rgba(255,255,255,0.12)' }} />
              </div>
            );
          })}
        </>
      )}

      {atribuirId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={() => setAtribuirId(null)}>
          <div className="bg-white rounded-lg shadow-xl w-64 max-h-80 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-3 py-2 border-b font-medium text-sm text-gray-700 flex items-center justify-between">
              Atribuir agente
              <button onClick={() => setAtribuirId(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
            </div>
            <button onClick={() => atribuir(atribuirId!, null)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 text-gray-500">
              Sem atribuicao
            </button>
            {semPontoHoje && (
              <div className="px-3 py-2 text-xs border-b" style={{ backgroundColor: '#fde68a', color: '#000' }}>
                Ninguem do departamento <b>{atribuirInbox ? atribuirInbox.charAt(0).toUpperCase() + atribuirInbox.slice(1) : ''}</b> bateu ponto hoje. Mostrando todas as vinculadas a esse departamento.
              </div>
            )}
            {agentes.map((ag) => (
              <button key={ag.id} onClick={() => atribuir(atribuirId!, ag.id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100">
                <Avatar nome={ag.nome} fotoUrl={ag.foto} tamanho={24} />
                {ag.nome.replace(/\s+FIODECOBRE$/i, '')}
              </button>
            ))}
          </div>
        </div>
      )}

      {statusId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={() => setStatusId(null)}>
          <div className="bg-white rounded-lg shadow-xl w-64 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-3 py-2 border-b font-medium text-sm text-gray-700 flex items-center justify-between">
              Alterar status do chamado
              <button onClick={() => setStatusId(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
            </div>
            <div className="p-2 flex flex-col gap-1.5">
              {STATUS_CHAMADO.map((s) => {
                const info = STATUS_INFO[s];
                return (
                  <button
                    key={s}
                    onClick={() => mudarStatus(statusId!, s)}
                    className="w-full text-left px-2 py-1.5 rounded text-sm font-bold hover:opacity-90 transition"
                    style={{ background: info.bg, color: info.fg }}
                  >
                    {info.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ItemMenu({ icon, label, onClick }: { icon: ReactElement; label: string; onClick: (e: React.MouseEvent) => void }) {
  return (
    <button onClick={onClick} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-100 transition">
      {icon} {label}
    </button>
  );
}
